import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Gem, Mail, ArrowRight, Check, Loader2 } from "lucide-react";

export default function VerifyEmailGate() {
  const { user, logout, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const resend = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      setTimeout(() => setSent(false), 8000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not resend. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="verify-email-gate">
      <div className="glass-panel rounded-3xl p-10 lg:p-14 max-w-lg w-full relative overflow-hidden">
        <div className="crystal-glow opacity-50" />
        <div className="relative">
          <div className="flex items-center gap-2.5 mb-8">
            <Gem className="w-7 h-7 text-gold" />
            <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
          </div>

          <div className="w-12 h-12 rounded-full bg-yellow-300/15 border border-yellow-300/40 flex items-center justify-center mb-6">
            <Mail className="w-6 h-6 text-gold" />
          </div>

          <h1 className="font-display text-3xl text-white mb-3 tracking-tight">Verify your email</h1>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            We just sent a verification link to <span className="text-white font-medium">{user?.email}</span>. Click it to unlock your dashboard.
          </p>

          <div className="space-y-3">
            <button
              onClick={refresh}
              className="btn-primary w-full"
              data-testid="gate-i-verified"
            >
              I've verified · refresh <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={resend}
              disabled={busy || sent}
              className="btn-ghost w-full"
              data-testid="gate-resend"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> :
                sent ? <><Check className="w-4 h-4" /> Email sent — check your inbox</> :
                "Resend verification email"}
            </button>

            {error && (
              <div className="text-sm text-rose-400 text-center" data-testid="gate-error">{error}</div>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-white/5 text-center">
            <button
              onClick={async () => { await logout(); window.location.href = "/login"; }}
              className="text-xs text-zinc-500 hover:text-white transition-colors"
              data-testid="gate-logout"
            >
              Sign in with a different account
            </button>
          </div>

          <p className="text-xs text-zinc-600 mt-6 leading-relaxed">
            Didn't get the email? Check spam, or wait a minute and resend. The link expires in 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
